import stringsAr from './strings.ar.js'
import stringsEn from './strings.en.js'

export const LOCALE_STORAGE_KEY = 'saloon_erp_ui_locale'

/** @typedef {'en' | 'ar'} UiLocale */

/** @type {Record<UiLocale, Record<string, unknown>>} */
export const UI_STRINGS = {
  en: stringsEn,
  ar: stringsAr,
}

/** @type {UiLocale[]} */
export const SUPPORTED_LOCALES = ['en', 'ar']

/**
 * @param {string | null | undefined} value
 * @returns {UiLocale}
 */
export function normalizeLocale(value) {
  return value === 'ar' ? 'ar' : 'en'
}

/**
 * @returns {UiLocale}
 */
export function getStoredLocale() {
  if (typeof window === 'undefined') {
    return 'en'
  }

  return normalizeLocale(window.localStorage.getItem(LOCALE_STORAGE_KEY))
}

/**
 * @param {UiLocale} locale
 */
export function setStoredLocale(locale) {
  if (typeof window === 'undefined') {
    return
  }

  window.localStorage.setItem(LOCALE_STORAGE_KEY, normalizeLocale(locale))
}

/**
 * @param {Record<string, unknown>} tree
 * @param {string[]} parts
 * @returns {string | undefined}
 */
function lookupString(tree, parts) {
  /** @type {unknown} */
  let cursor = tree

  for (const part of parts) {
    if (cursor == null || typeof cursor !== 'object' || !(part in cursor)) {
      return undefined
    }

    cursor = /** @type {Record<string, unknown>} */ (cursor)[part]
  }

  return typeof cursor === 'string' ? cursor : undefined
}

/**
 * @param {string} key
 * @param {UiLocale} locale
 * @param {Record<string, string | number>} [params]
 * @returns {string}
 */
export function translate(key, locale = 'en', params = {}) {
  const parts = key.split('.')
  const primary = lookupString(UI_STRINGS[normalizeLocale(locale)], parts)
  const fallback = locale === 'en' ? undefined : lookupString(UI_STRINGS.en, parts)
  let value = primary ?? fallback ?? key

  for (const [name, replacement] of Object.entries(params)) {
    value = value.replaceAll(`{{${name}}}`, String(replacement))
  }

  return value
}

/**
 * @param {UiLocale} locale
 * @returns {string}
 */
export function localeDocumentLang(locale) {
  return normalizeLocale(locale) === 'ar' ? 'ar' : 'en'
}

/**
 * @param {UiLocale} locale
 * @returns {'ltr' | 'rtl'}
 */
export function localeDocumentDir(locale) {
  return normalizeLocale(locale) === 'ar' ? 'rtl' : 'ltr'
}
